import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchItineraryPlaybook } from '../../services/cms/playbooks';
import {
  buildEventParamsFromAttribution,
  saveItineraryAttribution,
  trackEvent,
} from '../../utils/analytics';
import { portableTextToPlainText } from '../../utils/portableText';
import { urlFor } from '../../lib/sanityImage';
import { useLayoutCtas } from '../../components/shared/layout';
import ItineraryPlaybookPageAdapter from './ItineraryPlaybookPageAdapter';
import { buildRuntimeCanonicalUrl, getRuntimePublicBaseUrl } from '../../lib/siteUrls';
import { getRuntimeSiteContext } from '../../lib/siteContext';
import {
  buildRuntimePlaybookPath,
  getRuntimeItineraryLabel,
  getRuntimePlaybookBasePath,
  normalizePlaybookSlugPath,
} from '../../lib/playbookRoutes';

const DEFAULT_CTA_PATH = '/interview/wizard';
const CTA_SRC_ORGANIC = 'organic_spoke';
const CTA_SRC_ADS = 'ads_spoke';

function safeDecodeSlug(value) {
  if (!value) {
    return '';
  }

  try {
    return decodeURIComponent(value);
  } catch (error) {
    console.warn('Failed to decode slug segment from itinerary playbook path', { value, error });
    return value;
  }
}

function normalizeSlugPath(pathname, basePath) {
  const prefix = basePath;
  if (!pathname.startsWith(prefix)) {
    return { slugPath: '', needsRedirect: false };
  }

  const remainder = pathname.slice(prefix.length);
  const trimmedLeading = remainder.replace(/^\/+/, '');
  const decoded = safeDecodeSlug(trimmedLeading);
  const normalized = normalizePlaybookSlugPath(decoded);
  const needsRedirect = decoded !== normalized && normalized.length > 0;

  return { slugPath: normalized, needsRedirect };
}

function deriveSrcParam(searchParams) {
  const existing = searchParams.get('src');
  if (existing) {
    return existing;
  }

  const medium = (searchParams.get('utm_medium') || '').toLowerCase();
  if (['cpc', 'paid', 'ppc', 'sem', 'ads'].includes(medium)) {
    return CTA_SRC_ADS;
  }

  return CTA_SRC_ORGANIC;
}

function collectAttributionParams(searchParams, slugPath, basePath) {
  const preserved = new URLSearchParams();

  searchParams.forEach((value, key) => {
    if (key.startsWith('utm_') || key === 'gclid' || key === 'src' || key === 'spoke') {
      preserved.set(key, value);
    }
  });

  if (slugPath) {
    preserved.set('spoke', slugPath);
  }

  const landingPath = slugPath ? `${basePath}/${slugPath}` : basePath;
  preserved.set('landingPath', landingPath);
  preserved.set('src', deriveSrcParam(searchParams));

  return preserved;
}

function buildCtaUrl(baseUrl, params, siteUrl) {
  const hasProtocol = /^https?:\/\//i.test(baseUrl);
  const target = hasProtocol ? new URL(baseUrl) : new URL(baseUrl, siteUrl);

  params.forEach((value, key) => {
    if (value != null && value !== '') {
      target.searchParams.set(key, value);
    }
  });

  if (hasProtocol) {
    return target.toString();
  }

  const path = target.pathname.endsWith('/') ? target.pathname.slice(0, -1) : target.pathname;
  const search = target.searchParams.toString();
  return search ? `${path}?${search}` : path;
}

function buildBreadcrumbJsonLd(playbook, slugPath, siteUrl, basePath, itineraryLabel) {
  if (!playbook || !slugPath) {
    return null;
  }

  const canonicalPath = `${basePath}/${slugPath}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: itineraryLabel,
        item: `${siteUrl}${basePath}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: playbook.title,
        item: `${siteUrl}${canonicalPath}`,
      },
    ],
  };
}

function buildFaqJsonLd(playbook) {
  if (!playbook || !Array.isArray(playbook.faq) || playbook.faq.length === 0) {
    return null;
  }

  const mainEntity = playbook.faq
    .map((faq) => {
      const answerText = portableTextToPlainText(faq.answer);
      if (!faq.question || !answerText) {
        return null;
      }
      return {
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answerText,
        },
      };
    })
    .filter(Boolean);

  if (mainEntity.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
}

function buildWebPageJsonLd(playbook, canonicalUrl, metaDescription, heroImageUrl, modifiedDate) {
  if (!playbook || !canonicalUrl) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: playbook.title,
    description: metaDescription || playbook.hero?.subhead || playbook.title,
    url: canonicalUrl,
    inLanguage: 'en-US',
    ...(heroImageUrl ? { primaryImageOfPage: heroImageUrl } : {}),
    ...(modifiedDate ? { dateModified: modifiedDate } : {}),
  };
}

const sanitizeEventParams = (params = {}) => {
  const cleaned = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    cleaned[key] = typeof value === 'number' ? value : String(value);
  });
  return cleaned;
};

const identifyAxisDetails = (axis) => {
  if (!axis || typeof axis !== 'object') {
    return { type: null, value: null };
  }

  for (const [key, value] of Object.entries(axis)) {
    if (value !== null && value !== undefined && value !== '') {
      return {
        type: key,
        value: typeof value === 'number' ? value : String(value),
      };
    }
  }

  return { type: null, value: null };
};

export default function ItineraryPlaybookRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const { islandConfig } = useMemo(() => getRuntimeSiteContext(), []);
  const playbookBasePath = useMemo(() => getRuntimePlaybookBasePath(), []);
  const itineraryLabel = useMemo(() => getRuntimeItineraryLabel(), []);
  const [loading, setLoading] = useState(true);
  const [playbook, setPlaybook] = useState(null);
  const [error, setError] = useState(null);
  const viewTrackedRef = useRef(false);
  const {
    setHeaderCta = () => {},
    resetHeaderCta = () => {},
    setFooterCta = () => {},
    resetFooterCta = () => {},
  } = useLayoutCtas() || {};

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const siteUrl = getRuntimePublicBaseUrl();
  const { slugPath, needsRedirect } = useMemo(
    () => normalizeSlugPath(location.pathname, playbookBasePath),
    [location.pathname, playbookBasePath],
  );

  useEffect(() => {
    if (!slugPath) {
      navigate(playbookBasePath, { replace: true });
      return;
    }

    if (needsRedirect) {
      const redirectTarget = `${playbookBasePath}/${slugPath}${location.search || ''}`;
      navigate(redirectTarget, { replace: true });
    }
  }, [location.search, navigate, needsRedirect, playbookBasePath, slugPath]);

  useEffect(() => {
    let isActive = true;

    async function loadPlaybook() {
      if (!slugPath) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await fetchItineraryPlaybook(slugPath);
        if (!isActive) {
          return;
        }

        setPlaybook(data || null);
      } catch (fetchError) {
        console.error('Failed to load itinerary playbook data', fetchError);
        if (isActive) {
          setError(fetchError);
          setPlaybook(null);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    }

    loadPlaybook();

    return () => {
      isActive = false;
    };
  }, [slugPath]);

  const attributionParams = useMemo(
    () => collectAttributionParams(searchParams, slugPath, playbookBasePath),
    [playbookBasePath, searchParams, slugPath],
  );
  const axisDetails = useMemo(
    () => identifyAxisDetails(playbook?.axis),
    [playbook?.axis],
  );
  const baseAttribution = useMemo(
    () => buildEventParamsFromAttribution(attributionParams),
    [attributionParams],
  );
  const landingPath = buildRuntimePlaybookPath(slugPath);
  const playbookTitle = playbook?.title || null;
  const viewEventParams = useMemo(
    () =>
      sanitizeEventParams({
        ...baseAttribution,
        landing_path: baseAttribution.landing_path || landingPath,
        slug_path: slugPath || '',
        axis_type: axisDetails.type,
        axis_value: axisDetails.value,
        playbook_title: playbookTitle || undefined,
      }),
    [axisDetails.type, axisDetails.value, baseAttribution, landingPath, playbookTitle, slugPath],
  );

  const primaryCtaLabel = playbook?.hero?.primaryCtaLabel || 'Plan Your Trip';
  const finalCtaLabel = playbook?.hero?.primaryCtaLabel || 'Start Interview';

  useEffect(() => {
    viewTrackedRef.current = false;
  }, [slugPath]);

  const ctaUrl = useMemo(() => {
    const baseUrl = playbook?.hero?.primaryCtaUrlOverride || DEFAULT_CTA_PATH;
    return buildCtaUrl(baseUrl, attributionParams, siteUrl);
  }, [attributionParams, playbook?.hero?.primaryCtaUrlOverride, siteUrl]);

  useEffect(() => {
    if (!playbook || viewTrackedRef.current) return;
    trackEvent('itinerary_playbook_view', viewEventParams);
    saveItineraryAttribution(viewEventParams);
    viewTrackedRef.current = true;
  }, [playbook, viewEventParams]);

  const handleCtaClick = useCallback(
    (locationKey, labelOverride) => {
      if (!ctaUrl) return;
      const label = labelOverride || primaryCtaLabel;
      const eventPayload = sanitizeEventParams({
        ...viewEventParams,
        cta_location: locationKey,
        cta_label: label,
        cta_url: ctaUrl,
      });
      saveItineraryAttribution(eventPayload);
      trackEvent('itinerary_cta_click', eventPayload);
      setTimeout(() => {
        window.location.assign(ctaUrl);
      }, 50);
    },
    [ctaUrl, primaryCtaLabel, viewEventParams],
  );

  const helmetTitle = playbook?.seo?.titleTag
    ? playbook.seo.titleTag
    : playbook
    ? `${playbook.title} | ${itineraryLabel} Playbook | Alaka‘i Aloha`
    : `${itineraryLabel} Playbook`;
  const metaDescription = playbook?.seo?.metaDescription || playbook?.hero?.subhead || '';
  const canonicalUrl = buildRuntimeCanonicalUrl(buildRuntimePlaybookPath(slugPath));
  const heroImageUrl = playbook?.hero?.image?.asset
    ? urlFor(playbook.hero.image).width(1600).height(900).fit('crop').auto('format').url()
    : null;
  const modifiedDate = playbook?.lastReviewed || playbook?._updatedAt?.slice(0, 10) || null;
  const faqJsonLd = useMemo(() => buildFaqJsonLd(playbook), [playbook]);
  const breadcrumbsJsonLd = useMemo(
    () => buildBreadcrumbJsonLd(playbook, slugPath, siteUrl, playbookBasePath, itineraryLabel),
    [itineraryLabel, playbook, playbookBasePath, siteUrl, slugPath],
  );
  const webPageJsonLd = useMemo(
    () => buildWebPageJsonLd(playbook, canonicalUrl, metaDescription, heroImageUrl, modifiedDate),
    [canonicalUrl, heroImageUrl, metaDescription, modifiedDate, playbook],
  );

  useEffect(() => {
    if (!ctaUrl) return;
    setHeaderCta({ label: primaryCtaLabel, href: ctaUrl, onClick: handleCtaClick });
    setFooterCta({ label: finalCtaLabel, href: ctaUrl, onClick: handleCtaClick });
    return () => {
      resetHeaderCta();
      resetFooterCta();
    };
  }, [
    ctaUrl,
    finalCtaLabel,
    handleCtaClick,
    primaryCtaLabel,
    resetFooterCta,
    resetHeaderCta,
    setFooterCta,
    setHeaderCta,
  ]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-lg text-slate-200 animate-pulse">Gathering your playbook...</div>
      </div>
    );
  }

  if (!loading && (!playbook || error)) {
    return (
      <>
        <Helmet>
          <title>{`${itineraryLabel} Playbook Not Found | Alaka‘i Aloha`}</title>
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href={buildRuntimeCanonicalUrl(playbookBasePath)} />
        </Helmet>
        <div className="min-h-[60vh] flex items-center justify-center px-6">
          <div className="max-w-2xl text-center text-slate-100">
            <h1 className="text-3xl font-semibold mb-4">We couldn’t find that playbook.</h1>
            <p className="text-lg text-slate-300 mb-6">
              The {itineraryLabel.toLowerCase()} you’re looking for isn’t published yet. Explore featured itineraries or start with
              the main guide for {islandConfig.shortTitle}.
            </p>
            <div className="flex flex-col md:flex-row gap-3 justify-center">
              <Link
                to={playbookBasePath}
                className="inline-flex items-center justify-center rounded-full bg-sky-500 px-6 py-3 font-semibold text-slate-950 hover:bg-sky-400 transition"
              >
                {`Visit the main ${itineraryLabel.toLowerCase()} page`}
              </Link>
              <Link
                to="/featured"
                className="inline-flex items-center justify-center rounded-full border border-sky-400 px-6 py-3 font-semibold text-sky-200 hover:text-sky-100 hover:border-sky-200 transition"
              >
                See featured itineraries
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{helmetTitle}</title>
        {metaDescription && <meta name="description" content={metaDescription} />}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={helmetTitle} />
        {metaDescription && <meta property="og:description" content={metaDescription} />}
        <meta property="og:site_name" content="Alaka'i Aloha" />
        {heroImageUrl && <meta property="og:image" content={heroImageUrl} />}
        {playbook?.hero?.imageAlt && <meta property="og:image:alt" content={playbook.hero.imageAlt} />}
        <meta name="twitter:card" content={heroImageUrl ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={helmetTitle} />
        {metaDescription && <meta name="twitter:description" content={metaDescription} />}
        {heroImageUrl && <meta name="twitter:image" content={heroImageUrl} />}
        {webPageJsonLd && <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>}
        {faqJsonLd && <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>}
        {breadcrumbsJsonLd && <script type="application/ld+json">{JSON.stringify(breadcrumbsJsonLd)}</script>}
      </Helmet>

      <ItineraryPlaybookPageAdapter
        playbook={playbook}
        primaryCtaLabel={primaryCtaLabel}
        finalCtaLabel={finalCtaLabel}
        finalCtaHeading={`Ready to plan your ${islandConfig.shortTitle} trip?`}
        onCtaClick={handleCtaClick}
      />
    </>
  );
}
